import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowSquareOutIcon,
  CheckCircleIcon,
  CoinsIcon,
  LifebuoyIcon,
  SignatureIcon,
  SpinnerIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';

import {
  forwardWalletBatch,
  getRescueToEoaTypedData,
  getRescueToPusdTypedData,
  type RescueItem,
  type RescueToEoaTypedDataResponse,
  type RescueToPusdTypedDataResponse,
} from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';
import { getSigner } from '../wallet';

type Mode = 'eoa' | 'pusd';
type RescueData = RescueToEoaTypedDataResponse | RescueToPusdTypedDataResponse;

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: RescueData }
  | { kind: 'signing' }
  | { kind: 'submitting' }
  | { kind: 'submitted'; transactionHash: string; state: string };

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

function fmtItem(item: RescueItem): string {
  const n = Number(item.balance);
  if (!Number.isFinite(n)) return item.balance;
  return item.decimals >= 18 ? n.toFixed(4) : n.toFixed(2);
}

export default function Rescue() {
  const auth = useAuth();
  const t = useT();
  const { mode: modeParam } = useParams<{ mode: string }>();
  const mode: Mode = modeParam === 'pusd' ? 'pusd' : 'eoa';
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useTelegramBackButton();
  useNavStackEntry({ label: t('rescue.breadcrumb') });

  useEffect(() => {
    if (auth.state !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        const data =
          mode === 'pusd'
            ? await getRescueToPusdTypedData(auth.auth.externalUserId)
            : await getRescueToEoaTypedData(auth.auth.externalUserId);
        if (!cancelled) setPhase({ kind: 'ready', data });
      } catch (err) {
        if (!cancelled)
          setPhase({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.state, auth.state === 'ready' ? auth.auth.externalUserId : null, mode]);

  async function onSign() {
    if (phase.kind !== 'ready' || auth.state !== 'ready') return;
    const { typedData, args } = phase.data;
    try {
      setPhase({ kind: 'signing' });
      const signer = await getSigner();
      const types = { ...typedData.types };
      delete (types as Record<string, unknown>)['EIP712Domain'];
      const signature = await signer.signTypedData(
        typedData.domain as never,
        types,
        typedData.message,
      );

      setPhase({ kind: 'submitting' });
      const resp = await forwardWalletBatch(auth.auth.externalUserId, {
        type: 'WALLET',
        from: args.from,
        to: args.factoryAddress,
        nonce: args.nonce,
        signature,
        depositWalletParams: {
          depositWallet: args.walletAddress,
          deadline: args.deadline,
          calls: args.calls,
        },
      });
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
      setPhase({
        kind: 'submitted',
        transactionHash: resp.transactionHash,
        state: resp.state,
      });
    } catch (err) {
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
      setPhase({ kind: 'error', message: (err as Error).message });
    }
  }

  const titleKey = mode === 'pusd' ? 'rescue.pusd.title' : 'rescue.eoa.title';
  const descKey = mode === 'pusd' ? 'rescue.pusd.desc' : 'rescue.eoa.desc';
  const ctaIdle = mode === 'pusd' ? 'rescue.pusd.cta' : 'rescue.eoa.cta';

  const destLabel = useMemo(() => {
    if (phase.kind !== 'ready') return null;
    if (mode === 'pusd') return t('rescue.pusd.dest_value');
    const dest = (phase.data as RescueToEoaTypedDataResponse).destination;
    return short(dest);
  }, [phase, mode, t]);

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('rescue.preparing_title')}
        description={t('rescue.preparing_desc')}
        shortDescription={t('rescue.preparing_short')}
      />
    );
  }
  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow="Rescue" title={t(titleKey)}>
        <Alert tone="danger">
          <AlertTitle>
            {t('common.auth_error', { error: '' }) || 'Authentication failed'}
          </AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }
  if (phase.kind === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('rescue.scanning_title')}
        description={t('rescue.scanning_desc')}
        shortDescription={t('rescue.scanning_short')}
      />
    );
  }
  if (phase.kind === 'error') {
    return (
      <ScreenShell eyebrow="Rescue" title={t(titleKey)}>
        <Alert tone="danger">
          <AlertTitle>{t('rescue.failed')}</AlertTitle>
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="lg"
          onClick={() => tryGetTelegram()?.close?.()}
        >
          {t('common.back_to_chat')}
        </Button>
      </ScreenShell>
    );
  }
  if (phase.kind === 'submitted') {
    const url = phase.transactionHash
      ? `https://polygonscan.com/tx/${phase.transactionHash}`
      : null;
    return (
      <ScreenShell eyebrow="Rescue" title={t('rescue.submitted_title')}>
        <Card>
          <CardHeader>
            <CardEyebrow>
              <CheckCircleIcon weight="fill" className="size-3" />
              {t('rescue.submitted_badge')}
            </CardEyebrow>
            <CardTitle>{t('rescue.submitted_body')}</CardTitle>
            <CardDescription>{t('rescue.submitted_desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <KeyValue
              label={t('rescue.state_label')}
              value={<Badge tone="success">{phase.state}</Badge>}
            />
          </CardContent>
          <CardFooter>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'outline', size: 'lg' })}
              >
                <ArrowSquareOutIcon weight="bold" className="size-4" />
                {t('rescue.view_on_polygonscan')}
              </a>
            )}
            <Button size="lg" onClick={() => tryGetTelegram()?.close?.()}>
              {t('common.back_to_chat')}
            </Button>
          </CardFooter>
        </Card>
      </ScreenShell>
    );
  }

  const data = phase.kind === 'ready' ? phase.data : null;
  const busy = phase.kind === 'signing' || phase.kind === 'submitting';
  const Icon = mode === 'pusd' ? CoinsIcon : LifebuoyIcon;

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <Icon weight="fill" className="size-3" />
          Rescue
        </span>
      }
      title={t(titleKey)}
      description={t(descKey)}
    >
      <NavBar />
      <Card>
        <CardHeader>
          <CardEyebrow>
            <WarningIcon weight="fill" className="size-3" />
            {t('rescue.detected_label')}
          </CardEyebrow>
          <CardTitle className="text-xl">{t('rescue.detected_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="my-1 flex flex-col gap-1.5 font-mono text-[14px]">
            {(data?.items ?? []).map((it: RescueItem) => (
              <li
                key={it.symbol}
                className="tabular-nums flex items-baseline justify-between"
              >
                <span>
                  <strong>{fmtItem(it)}</strong> {it.symbol}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <KeyValue
              label={t('rescue.dest_label')}
              value={destLabel ?? '—'}
              mono={mode === 'eoa'}
            />
            <KeyValue label={t('rescue.gas_label')} value={<Badge tone="success">Gasless</Badge>} />
          </div>
        </CardContent>
        <CardFooter>
          <Button size="lg" onClick={onSign} disabled={phase.kind !== 'ready'}>
            {busy ? (
              <SpinnerIcon weight="bold" className="size-4 animate-spin" />
            ) : (
              <SignatureIcon weight="bold" className="size-4" />
            )}
            {phase.kind === 'signing'
              ? t('rescue.signing')
              : phase.kind === 'submitting'
                ? t('rescue.submitting')
                : t(ctaIdle)}
          </Button>
        </CardFooter>
      </Card>
    </ScreenShell>
  );
}
